import { useEffect, useState } from "react";
import { runFlow, getParameters } from "../api";
import {
  Table,
  TextInput,
  Button,
  Select,
  Title,
  Group,
  Center,
  Loader,
  Alert,
  Paper,
  Text,
  Badge,
  ThemeIcon,
  Stack,
  List,
} from "@mantine/core";
import {
  IconBrain,
  IconCoin,
  IconClock,
  IconAlertTriangle,
  IconCircleCheck,
  IconHierarchy3,
} from "@tabler/icons-react";
import { useForm } from "@mantine/form";
import ModelSelect from "../_Common/ModelSelect";
import RequestViewer from "./RequestViewer";

export default function FlowRun({ flow, tokens, version }) {
  const [runs, setRuns] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [entrypoint, setEntrypoint] = useState(null);
  const [parameters, setParameters] = useState(tokens);

  const environmentOptions = flow?.activeVersions
    ? Object.keys(flow.activeVersions).map((env) => ({ value: env, label: env }))
    : [];

  useEffect(() => {
    // getFlowRuns(flow.id).then((data) => {
    //   setRuns(data);
    // });
  }, [flow.id]);

  useEffect(() => {
    form.setFieldValue("entrypoint", entrypoint);
    getParameters(flow.id, version.id, entrypoint)
      .then((data) => {
        setParameters(data);
      })
      // Invalid [[parameter]] syntax makes this 400; api.js shows the message, so fall back to none.
      .catch(() => setParameters([]));
      // Mantine form object has unstable identity; including it would rerun this effect every render.
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entrypoint, flow.id, version.id]);

  // Helper to calculate execution summary from agent tree
  const calculateExecutionSummary = (run) => {
    if (!run?.agentExecutionTree) return null;

    const tree = run.agentExecutionTree;
    let agentCount = 0;
    let maxDepth = 0;
    let failedCount = 0;
    let totalToolCalls = 0;

    const traverse = (node) => {
      agentCount++;
      if (node.depth > maxDepth) maxDepth = node.depth;
      if (node.status === "failed" || node.status === "error") failedCount++;
      if (node.toolCalls) totalToolCalls += node.toolCalls.length;
      if (node.children) node.children.forEach(traverse);
    };

    traverse(tree);

    return {
      agentCount,
      maxDepth,
      failedCount,
      totalToolCalls,
      totalCredits: tree.totalTreeCreditsUsed || 0,
      totalDuration: tree.durationMs || 0,
      status: tree.status,
    };
  };

  let entrypointOptions = version.templates.map((p) => ({
    value: p.name,
    label: p.name,
  }));

  const form = useForm({
    // mode: "uncontrolled",
    initialValues: {
      parameters: {},
      overrideModel: null,
      flowName: "",
      environment: flow?.activeVersions
        ? Object.keys(flow.activeVersions)[0] ?? ""
        : "",
      version: version.id,
      entrypoint: null,
      isDebug: true,
    },
  });

  return (
    <div>
      <form
        onSubmit={form.onSubmit((values) => {
          setIsRunning(true);
          runFlow(flow.id, values)
            .then((data) => {
              setRuns([data, ...runs]);
              setIsRunning(false);
            })
            .catch(() => {
              setIsRunning(false);
            });
          console.log(values);
        })}
      >
        <Group mt="md" align="flex-end">
          <Select
            data={environmentOptions}
            label="Environment"
            description="The activated environment to run against"
            disabled={isRunning}
            style={{ flex: 1 }}
            {...form.getInputProps("environment")}
          />

          <ModelSelect
            label="LLM Override"
            description="Overrides the default LLM"
            placeholder="Leave default"
            disabled={isRunning}
            clearable
            style={{ flex: 1 }}
            {...form.getInputProps("overrideModel")}
          />

          <Select
            data={entrypointOptions}
            label="Entrypoint"
            description="Overrides the default entrypoint"
            placeholder="Leave default"
            disabled={isRunning}
            style={{ flex: 1 }}
            value={entrypoint}
            onChange={setEntrypoint}
          />

          <Button disabled={isRunning} type="submit">
            Run!
          </Button>
        </Group>

        {parameters == null && <Loader />}

        {parameters != null && parameters.length == 0 && (
          <Alert mt="xl">No configurable parameters</Alert>
        )}

        {parameters != null && parameters.length > 0 && (
          <Table mt="xl">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Token</Table.Th>
                <Table.Th>Source</Table.Th>
                <Table.Th>Default</Table.Th>
                <Table.Th>Override</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {parameters.map((tok) => (
                <Table.Tr key={tok.token}>
                  <Table.Td>{tok.token}</Table.Td>
                  <Table.Td>
                    <Badge variant="light" size="sm">
                      {tok.source === "toolUrl"
                        ? "tool URL"
                        : tok.source === "userTemplate"
                          ? "user template"
                          : "template"}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {tok.promptTemplate ? "sub-template" : "none"}
                  </Table.Td>
                  <Table.Td>
                    <TextInput
                      {...form.getInputProps(`parameters.${tok.token}`)}
                    />
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </form>

      <Title mt="xl" mb="sm" order={4}>
        Run logs
      </Title>

      {isRunning && (
        <Center my="md">
          <Loader />
        </Center>
      )}

      {runs.map((run, index) => {
        const summary = calculateExecutionSummary(run);
        return (
          <div key={run.version + index}>
            {summary && (
              <Paper withBorder p="md" radius="sm" mb="md" bg={summary.failedCount > 0 ? "red.0" : "gray.0"}>
                <Group justify="space-between" align="flex-start">
                  <Stack gap={4}>
                    <Group gap="xs">
                      <ThemeIcon
                        size="sm"
                        variant="light"
                        color={summary.failedCount > 0 ? "red" : "green"}
                      >
                        {summary.failedCount > 0 ? (
                          <IconAlertTriangle size={14} />
                        ) : (
                          <IconCircleCheck size={14} />
                        )}
                      </ThemeIcon>
                      <Text size="sm" fw={600}>
                        Execution Summary
                      </Text>
                      {summary.failedCount > 0 && (
                        <Badge color="red" size="xs">
                          {summary.failedCount} failed
                        </Badge>
                      )}
                    </Group>
                  </Stack>
                  <Badge
                    variant="light"
                    color={summary.status === "completed" ? "green" : "red"}
                  >
                    {summary.status}
                  </Badge>
                </Group>

                <Group gap="xl" mt="md">
                  <Group gap={6}>
                    <ThemeIcon size="xs" variant="transparent" color="blue">
                      <IconBrain size={14} />
                    </ThemeIcon>
                    <Text size="sm" c="dimmed">
                      Agents:{" "}
                      <Text span fw={600}>
                        {summary.agentCount}
                      </Text>
                    </Text>
                  </Group>

                  <Group gap={6}>
                    <ThemeIcon size="xs" variant="transparent" color="grape">
                      <IconHierarchy3 size={14} />
                    </ThemeIcon>
                    <Text size="sm" c="dimmed">
                      Max Depth:{" "}
                      <Text span fw={600}>
                        {summary.maxDepth}
                      </Text>
                    </Text>
                  </Group>

                  <Group gap={6}>
                    <ThemeIcon size="xs" variant="transparent" color="cyan">
                      <IconClock size={14} />
                    </ThemeIcon>
                    <Text size="sm" c="dimmed">
                      Duration:{" "}
                      <Text span fw={600}>
                        {summary.totalDuration < 1000
                          ? `${summary.totalDuration}ms`
                          : `${(summary.totalDuration / 1000).toFixed(2)}s`}
                      </Text>
                    </Text>
                  </Group>

                  <Group gap={6}>
                    <ThemeIcon size="xs" variant="transparent" color="orange">
                      <IconCoin size={14} />
                    </ThemeIcon>
                    <Text size="sm" c="dimmed">
                      Credits:{" "}
                      <Text span fw={600}>
                        {summary.totalCredits?.toLocaleString()}
                      </Text>
                    </Text>
                  </Group>

                  {summary.totalToolCalls > 0 && (
                    <Text size="sm" c="dimmed">
                      Tool Calls:{" "}
                      <Text span fw={600}>
                        {summary.totalToolCalls}
                      </Text>
                    </Text>
                  )}
                </Group>
              </Paper>
            )}
            {run.warnings?.length > 0 && (
              <Alert
                icon={<IconAlertTriangle size={16} />}
                color="yellow"
                title="Parameter warnings"
                mb="md"
              >
                <List size="sm">
                  {run.warnings.map((w, i) => (
                    <List.Item key={i}>{w}</List.Item>
                  ))}
                </List>
              </Alert>
            )}
            <RequestViewer response={run} />
          </div>
        );
      })}
    </div>
  );
}
